import { DataType } from '@mode-switch/express';
import { Attr } from '@mode-switch/model';
import { DatePart } from '@mode-vegas/vegas';
import { ChartTypes } from '@mode/shared/contract-common';
import { CompiledFormula } from '@mode/shared/util-switch';
import { getDatePart, makeDatePartName, makeFormattedTimestamp } from './chart-date';

export function makeDateTruncValueFormatter(
  dateTruncField: CompiledFormula,
  chartFormat: ChartTypes.ChartFormat
): (name: any) => string {
  const datePart = getDatePart(dateTruncField);
  return (name: any) => {
    switch (dateTruncField.varType) {
      case Attr.Type.Discrete:
      case Attr.Type.Continuous:
        if (datePart != null) {
          return makeFormattedTimestamp(
            datePart,
            chartFormat.xAxis.dateFormat,
            name,
            false,
            dateTruncField.yearStart ?? dateTruncField.defaultYearStart
          );
        }

        return name;
    }
  };
}

export function makeDatePartValueFormatter(datePartField: CompiledFormula): (name: any) => string {
  return (name: any) => {
    switch (datePartField.varType) {
      case Attr.Type.Discrete:
        return name == null ? null : makeDatePartName(datePartField, name);
      case Attr.Type.Continuous:
        return name == null ? null : name;
    }
  };
}

export function makeSeriesNameFormatter(
  seriesField: CompiledFormula | undefined,
  chartFormat: ChartTypes.ChartFormat
): (name: any) => string | null {
  if (seriesField == null) {
    return (name: any) => (name == null ? null : name.toString());
  }

  if (seriesField.hasDatePart) {
    return makeDatePartValueFormatter(seriesField);
  }

  if (seriesField.hasDateTrunc) {
    return makeDateTruncValueFormatter(seriesField, chartFormat);
  }

  switch (seriesField.formulaDataType) {
    case DataType.Name.DateTime:
    case DataType.Name.Timestamp:
      // Exact Date and Time
      return (name: any) => (name == null ? null : makeFormattedTimestamp(DatePart.SECOND, 'hour', name));

    case DataType.Name.Boolean:
    default:
      return (name: any) => (name == null ? null : name.toString());
  }
}

/**
 * Helpers
 */

export function makeXValueFormatter(xField: CompiledFormula): (name: any) => string {
  if (xField.hasDatePart) {
    return makeDatePartValueFormatter(xField);
  }

  switch (xField.formulaDataType) {
    case DataType.Name.DateTime:
    case DataType.Name.Timestamp:
    case DataType.Name.Decimal:
    case DataType.Name.Integer:
    case DataType.Name.Float:
    case DataType.Name.Boolean:
      return (name: any) => name;
    default:
      return (name: any) => (name == null ? null : name.toString());
  }
}
